import * as React from "react";
import { IClientBooking, IClinician, RecurrenceRule } from "intake_shared/src/types";
import store from "../../../../store";
import moment from "../../../../utils/app_moment";
import { useToasts } from "react-toast-notifications";
import { AclContext } from "../../../../contexts/AclContext";
import { Dialog } from "../../bits/Dialog/Dialog";
import { Select } from "../../bits/Select/Select";
import styles from "./ClinicianAddBooking.module.scss";
import CustomDateSelector from "../../bits/CustomDateSelector/CustomDateSelector";

interface ClinicianAddBookingProps {
    prevBooking?: IClientBooking;
    onCancel: () => void;
    onComplete: () => Promise<void>;
    loggedInClinician: IClinician | undefined;
}

export function ClinicianAddBooking({
    prevBooking,
    onCancel,
    onComplete,
    loggedInClinician,
}: ClinicianAddBookingProps) {
    const { addToast } = useToasts();
    const [booking, setBooking] = React.useState<IClientBooking | undefined>(
        prevBooking
    );
    const loggedInUserEmail = React.useContext(AclContext)?.authUser.user?.email;

    React.useEffect(() => {
        setBooking(prevBooking);
    }, [prevBooking]);

    const handleCancel = () => {
        setBooking(undefined);
        onCancel();
    };

    const saveBooking = async () => {
        if (!booking) {
            return;
        }
        try {
            await store.addCaseloadBooking(
                booking.client_id,
                booking.clinician_id,
                { ...booking, ending_date: "" },
                loggedInUserEmail!
            );
            await onComplete();
            addToast("New appointment added", { appearance: "success" });
        } catch (err) {
            console.error(err);
            addToast("Failed to add new appointment", { appearance: "error" });
        }
    };

    if (!booking) {
        return null;
    }

    const startDateMoment = moment(booking.starting_date || booking.start);
    const startMoment = moment(booking.start);
    const endMoment = moment(booking.end);

    const recurrenceOptions = [
        { value: RecurrenceRule.WEEKLY, label: "Weekly" },
        { value: RecurrenceRule.BIWEEKLY, label: "Bi-Weekly" },
        { value: RecurrenceRule.TRIWEEKLY, label: "Every 3 weeks" },
        { value: RecurrenceRule.MONTHLY, label: "Monthly" },
        { value: RecurrenceRule.TWO_MONTHS, label: "Every 2 months" },
        { value: RecurrenceRule.THREE_MONTHS, label: "Every 3 months" },
    ];

    if (loggedInClinician?.departments?.includes("CHP")) {
        recurrenceOptions.push({ value: RecurrenceRule.ONE_OFF, label: "One-Off" });
    }

    return (
        <Dialog
            open={Boolean(prevBooking)}
            onClose={handleCancel}
            saveText="Add"
            onSave={saveBooking}
            title={`Add new appointment for ${booking.client_display_name}`}
        >
            <div className="v2-form">
                <div className={styles.field}>
                    <CustomDateSelector
                        showStartingDate={false}
                        defaultDate={{
                            day: startMoment.day(),
                            fromTime: startMoment.format("HH:mm"),
                            toTime: endMoment.format("HH:mm"),
                            startingDate: startDateMoment,
                            endingDate: null,
                        }}
                        onChange={(from, to) => {
                            setBooking((prevBooking) => {
                                if (prevBooking) {
                                    return {
                                        ...prevBooking,
                                        start: from.toISOString(),
                                        end: to.toISOString(),
                                    };
                                }
                                return prevBooking;
                            });
                        }}
                    />
                </div>

                <div className={styles.field}>
                    <Select
                        label="recurring"
                        value={recurrenceOptions.find(
                            (option) => option.value === booking.recurrenceRule
                        )}
                        onChange={(option) => {
                            if (
                                option &&
                                typeof option === "object" &&
                                "value" in option
                            ) {
                                setBooking({
                                    ...booking,
                                    recurrenceRule: option.value as RecurrenceRule,
                                });
                            }
                        }}
                        options={recurrenceOptions}
                        isLongListInDialog
                    />
                </div>
            </div>
        </Dialog>
    );
}
